/**
 * StatCard — a single headline metric: big number + label, with an optional
 * status badge + pipeline note for non-live stats ("blocked: GPU quota
 * pending"). Pure primitive: the value/status/note are resolved upstream (in
 * the website manifest layer, from inferrex-ai/stats-manifest.json) and passed
 * in. Composes Card + Badge. Tokens + Tailwind only.
 */
import type { ReactNode } from 'react';
import { Card } from '../card';
import { Badge } from '../badge';
import type { BadgeProps } from '../badge';

export type StatStatus = 'live' | 'placeholder' | 'projected' | 'blocked';

export interface StatCardProps {
  readonly value: ReactNode;
  readonly label: ReactNode;
  readonly status?: StatStatus;
  readonly note?: ReactNode;
  readonly description?: ReactNode;
}

const STATUS_VARIANT: Record<Exclude<StatStatus, 'live'>, NonNullable<BadgeProps['variant']>> = {
  placeholder: 'secondary',
  projected: 'outline',
  blocked: 'warning',
};

const STATUS_LABEL: Record<Exclude<StatStatus, 'live'>, string> = {
  placeholder: 'Placeholder',
  projected: 'Projected',
  blocked: 'Blocked',
};

export function StatCard({ value, label, status = 'live', note, description }: StatCardProps) {
  return (
    <Card className="flex flex-col items-center p-(--space-card) text-center">
      <div className="font-display text-3xl font-bold leading-none text-primary sm:text-4xl">{value}</div>
      {status !== 'live' && (
        <div className="mt-2"><Badge variant={STATUS_VARIANT[status]}>{STATUS_LABEL[status]}</Badge></div>
      )}
      <div className="mt-3 text-sm font-medium text-foreground">{label}</div>
      {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      {note && <p className="mt-2 text-xs text-muted-foreground">{note}</p>}
    </Card>
  );
}
